/**
 * Clone a nested campaign from clipboard data, then walk any campaigns
 * nested inside its missions.
 */

import { normalizeTaskEstimatedDuration } from '../../../../../../utils/taskDuration';
import { syncEstimatedMinutesFromClipboardPayload } from '../../../../../../utils/migrations/legacyEstimatedDuration.js';
import { generateId } from '../constants';
import { remapConditionEntry, remapDependsOnEntry } from '../dependencies';
import { newReminderId, normalizeReminders, restartRecurringReminder } from '../../../../../../utils/missionReminders';
import { DEFAULT_OPTIONS } from '../../../TaskNode/DecisionOptionsModal';
import { cloneAttachedDocsFromClipboard, cloneMissionVariableValues } from './payload';

/**
 * Clone a nested sprint from clipboard data with full ID remapping.
 * Preserves positions, connections (dependsOn/unlockConditions), and decision branches.
 */
export function cloneNestedSprint(nestedData, parentTaskId, timestamp) {
  const newNestedId = generateId('nested-sprint', timestamp);
  const now = new Date().toISOString();

  // First pass: generate new IDs and build the index->newId map
  const idMap = new Map();       // task index -> new task ID
  const optionIdMap = new Map(); // "taskIndex-optionIndex" -> new option ID

  const clonedTasks = (nestedData.tasks || []).map((nestedTask, idx) => {
    const tags = nestedTask.tags || [];
    const category = tags.length > 0 ? tags[0] : nestedTask.category;
    const nodeType = nestedTask.nodeType || 'task';
    const newId = generateId('task', timestamp, 'nested', idx);
    idMap.set(idx, newId);

    const subtasks = (nestedTask.subtasks || []).map((st, stIdx) => ({
      id: generateId('subtask', timestamp, 'nested', idx, stIdx),
      name: st.name || '',
      completed: st.completed || false,
    }));

    const deliverables = (nestedTask.deliverables || []).map((d, dIdx) => ({
      id: generateId('deliverable', timestamp, 'nested', idx, dIdx),
      name: d.name || '',
      completed: d.completed || false,
    }));

    const inputs = (nestedTask.inputs || []).map((inp, inpIdx) => ({
      id: generateId('input', timestamp, 'nested', idx, inpIdx),
      name: inp.name || '',
      completed: inp.completed || false,
      sourceType: 'external',
    }));

    let clonedOptions;
    if (nodeType === 'decision') {
      const sourceOptions = nestedTask.options?.length > 0 ? nestedTask.options : DEFAULT_OPTIONS;
      clonedOptions = sourceOptions.map((opt, optIdx) => {
        const newOptId = generateId('option', timestamp, 'nested', idx, optIdx);
        optionIdMap.set(`${idx}-${optIdx}`, newOptId);
        return { id: newOptId, label: opt.label, color: opt.color };
      });
    }

    const { attachedDocs, docTicks, docFiles } = cloneAttachedDocsFromClipboard(nestedTask);

    return normalizeTaskEstimatedDuration({
      id: newId,
      name: nestedTask.name || '',
      order: idx + 1,
      groupId: nestedTask.groupId || null,
      dependsOn: [],
      unlockConditions: [],
      status: nestedTask.status || 'pending',
      x: undefined,
      y: undefined,
      createdAt: now,
      updatedAt: now,
      startedAt: nestedTask.startedAt || null,
      completedAt: nestedTask.completedAt || null,
      priority: nestedTask.priority,
      difficulty: nestedTask.difficulty,
      nodeType,
      category,
      tags: tags.length > 0 ? tags : undefined,
      icon: nestedTask.icon,
      notes: nestedTask.notes,
      internalNotes: nestedTask.internalNotes ?? nestedTask.externalNotes,
      dueDate: nestedTask.dueDate || null,
      estimatedDurationMinutes: syncEstimatedMinutesFromClipboardPayload(nestedTask),
      reminders: (() => {
        const list = normalizeReminders(nestedTask.reminders).map((r) => restartRecurringReminder({
          ...r,
          id: newReminderId(),
          createdAt: now,
        }, Number(now) || Date.now()));
        return list.length ? list : undefined;
      })(),
      subtasks: subtasks.length > 0 ? subtasks : undefined,
      deliverables: deliverables.length > 0 ? deliverables : undefined,
      inputs: inputs.length > 0 ? inputs : undefined,
      attachedDocs,
      docTicks,
      docFiles,
      variableValues: cloneMissionVariableValues(nestedTask),
      docsMigratedAt: nestedTask.docsMigrated ? now : undefined,
      options: clonedOptions,
      answer: (nestedTask.answer !== undefined && nestedTask.answer !== null)
        ? (optionIdMap.get(`${idx}-${nestedTask.answer}`) || null)
        : null,
    });
  });

  // Second pass: remap dependencies using the index->newId maps
  clonedTasks.forEach((task, idx) => {
    const original = (nestedData.tasks || [])[idx];
    if (!original) return;

    // Milestone host attachment: index -> new host task ID
    if (task.nodeType === 'milestone' && typeof original.milestoneAnchor?.task === 'number') {
      const newHostId = idMap.get(original.milestoneAnchor.task);
      const side = original.milestoneAnchor.side;
      if (newHostId && (side === 'input' || side === 'output')) {
        task.milestoneAnchor = { hostTaskId: newHostId, side };
      }
    }

    if (task.nodeType === 'note' && typeof original.noteAnchor?.task === 'number') {
      const newHostId = idMap.get(original.noteAnchor.task);
      if (newHostId) {
        task.noteAnchor = { hostTaskId: newHostId };
      }
    }

    if (Array.isArray(original.dependsOn) && original.dependsOn.length > 0) {
      task.dependsOn = original.dependsOn
        .map(dep => remapDependsOnEntry(dep, idMap, optionIdMap))
        .filter(Boolean);
    }

    if (original.unlockConditions) {
      if (original.unlockConditions.version === 2 && original.unlockConditions.blocks) {
        const remappedBlocks = original.unlockConditions.blocks.map(block => {
          const conds = (block.conditions || [])
            .map(cond => remapConditionEntry(cond, idMap, optionIdMap))
            .filter(Boolean);
          if (conds.length === 0) return null;
          return { conditions: conds, innerOperator: block.innerOperator || 'AND', outerOperator: block.outerOperator || null };
        }).filter(Boolean);

        if (remappedBlocks.length > 0) {
          task.unlockConditions = { version: 2, blocks: remappedBlocks };
        }
      } else if (Array.isArray(original.unlockConditions) && original.unlockConditions.length > 0) {
        task.unlockConditions = original.unlockConditions.map(cond => {
          const entry = remapConditionEntry(cond, idMap, optionIdMap);
          if (!entry) return null;
          return { ...entry, operator: cond.operator || null };
        }).filter(Boolean);
      }
    }
  });

  return {
    id: newNestedId,
    name: nestedData.name || '',
    icon: nestedData.icon,
    parentTaskId,
    createdAt: now,
    tasks: clonedTasks,
    statuses: nestedData.statuses,
    categories: nestedData.categories,
    tags: nestedData.tags,
    groups: nestedData.groups,
    // Do not copy chain metadata — task IDs remap on paste, so stored chain ids are stale.
    customFields: nestedData.customFields,
    // Campaign variables — deep-copied so paste doesn't share rows with clipboard
    variables: Array.isArray(nestedData.variables) && nestedData.variables.length > 0
      ? nestedData.variables.map((v) => ({
          id: v.id,
          name: v.name,
          value: v.value,
          type: v.type || undefined,
          values: Array.isArray(v.values) ? [...v.values] : undefined,
          description: v.description,
          color: v.color,
          locked: v.locked || undefined,
        }))
      : undefined,
    kanbanColumnOrder: nestedData.kanbanColumnOrder,
    cardDisplaySettings: nestedData.cardDisplaySettings,
    views: nestedData.views,
    defaultViewSettings: nestedData.defaultViewSettings,
  };
}

/**
 * Clone a nested sprint and any nested sprints inside its tasks (arbitrary depth).
 * @returns {{ sprints: Array, rootSprint: Object }}
 */
export function cloneNestedSprintTree(nestedData, parentTaskId, parentSprintId, timestamp) {
  const cloned = cloneNestedSprint(nestedData, parentTaskId, timestamp);
  if (parentSprintId) {
    cloned.parentSprintId = parentSprintId;
  }

  const allSprints = [cloned];

  (nestedData.nestedSprints || []).forEach(childData => {
    const childTask = cloned.tasks?.[childData.forTask];
    if (!childTask) return;

    const { sprints, rootSprint } = cloneNestedSprintTree(
      childData,
      childTask.id,
      cloned.id,
      timestamp,
    );
    childTask.nestedSprintId = rootSprint.id;
    allSprints.push(...sprints);
  });

  return { sprints: allSprints, rootSprint: cloned };
}

/**
 * Attach cloned nested sprints to root-level pasted tasks from clipboard nestedSprints entries.
 */
export function attachNestedSprintsFromClipboard(clonedRootTasks, clipboardNestedSprints, timestamp) {
  const allNestedSprints = [];

  clonedRootTasks.forEach((task, idx) => {
    if (!task._needsNestedSprint) return;

    const nestedData = clipboardNestedSprints?.find(ns => ns.forTask === idx);
    if (!nestedData) return;

    const { sprints, rootSprint } = cloneNestedSprintTree(nestedData, task.id, null, timestamp);
    task.nestedSprintId = rootSprint.id;
    allNestedSprints.push(...sprints);
    delete task._needsNestedSprint;
  });

  return allNestedSprints;
}
